<script setup lang="ts">
import type { Todos } from "@classes-service-types/index";
import { format as formatDate, parse as parseDate } from "date-fns";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import CheckIcon from "primevue/icons/check";
import Tooltip, { type TooltipOptions } from "primevue/tooltip";
import { computed, toRefs } from "vue";
import { useTodos } from "@/components/modals/todos/composables/useTodos";
import type { SessionTodoForm } from "@/components/program-manager/sessions/components/TodosSection/types";
import editIcon from "@/images/icon-edit-material.svg";
import { TODOS_TABLE_COLUMNS } from "./constants";
import InstructionsHoverPopover from "./InstructionsHoverPopover.vue";
import type { TodosTableColumn, TodosTableProps } from "./types";
import UrlHoverPopover from "./UrlHoverPopover.vue";

defineOptions({
  name: "TodosTable",
});

const props = defineProps<TodosTableProps>();
const { items, headerLabelText, headerLabelClass, hasEditPermissions } = toRefs(props);

const emit = defineEmits<(e: "editTodo", todo: SessionTodoForm) => void>();

const needsTodosLookup = computed<boolean>(() =>
  (items.value ?? []).some((item) => !item.display_name && Boolean(item.todo_id)),
);

const { todos } = useTodos({ limit: 60 }, { enabled: needsTodosLookup });
const todosById = computed<Map<string, Todos>>(() => {
  const map = new Map<string, Todos>();
  for (const t of todos.value ?? []) {
    map.set(t.id, t);
  }
  return map;
});

const resolveDisplayName = (item: SessionTodoForm): string => {
  const todoDef = item.todo_id ? todosById.value.get(item.todo_id) : undefined;
  return item.display_name || todoDef?.default_display_name || todoDef?.title || "";
};

const vTooltip = Tooltip;
const tooltipSetup: TooltipOptions = {
  fitContent: true,
  class: "bg-white shadow rounded white max-w-xs",
  hideDelay: 300,
  pt: {
    text: "text-xs text-indigo-base p-3 leading-none break-words",
  },
};

// PrimeVue renders the header slot inside a flex div, so centring needs justify-content, not text-align.
const headerPt = (col: TodosTableColumn) => ({
  headerContent: { class: col.centered ? "justify-center" : "" },
});

const openEditModal = (todoData: SessionTodoForm): void => {
  if (hasEditPermissions.value) {
    emit("editTodo", todoData);
  }
};
</script>

<template>
  <DataTable
    v-if="items?.length > 0"
    :value="items"
    :pt="{
      table: {
        class: 'w-full',
      },
      header: {
        class: 'pb-2 px-0',
      },
      bodyRow: {
        class: 'bg-white hover:bg-[#EDF9F8]',
      },
      emptyMessageCell: {
        class: '',
      },
    }"
    sort-field="due_date"
    :sort-order="1"
    data-key="todo_id"
  >
    <template #header>
      <span class="text-xs font-semibold px-2 py-1 rounded-md uppercase" :class="headerLabelClass">
        {{ headerLabelText }}
      </span>
    </template>
    <Column
      v-for="col in TODOS_TABLE_COLUMNS"
      :key="col.field"
      :column-key="col.field"
      :field="col.field"
      header-class="text-sm font-semibold text-indigo-base bg-gray-50 !border-[#E4E7EC]"
      body-class="text-sm font-medium text-indigo-base !border-[#E4E7EC]"
      :reorderable-column="false"
      :pt="headerPt(col)"
    >
      <template #header>
        <div
          v-tooltip.top="{
            value: col.tooltip,
            id: 'todos-col-header-tooltip',
            ...tooltipSetup,
          }"
        >
          {{ col.label }}
        </div>
      </template>
      <template #body="slotProps">
        <span v-if="col.field === 'due_date'">
          {{ formatDate(parseDate(slotProps.data?.due_date, "yyyy-MM-dd", new Date()), "MM/dd/yyyy") }}
        </span>
        <span v-else-if="col.field === 'required'">
          <CheckIcon v-if="slotProps.data?.required" class="mx-auto h-[12px] w-[12px]" data-testid="required-check" />
        </span>
        <span v-else-if="col.field === 'instruction'">
          <InstructionsHoverPopover :instructions="slotProps.data?.instructions" />
        </span>
        <span v-else-if="col.field === 'supporting_url'">
          <UrlHoverPopover :url="slotProps.data?.supporting_url" />
        </span>
        <span v-else-if="col.field === 'display_name'">
          {{ resolveDisplayName(slotProps.data) }}
        </span>
        <span v-else-if="col.field === 'edit'">
          <img
            :src="editIcon"
            alt="edit icon"
            class="min-w-[24px]"
            :class="!hasEditPermissions ? 'opacity-30' : 'cursor-pointer'"
            @click="openEditModal(slotProps.data)"
          />
        </span>
        <span v-else>
          {{ slotProps.data?.[col.field] }}
        </span>
      </template>
    </Column>
  </DataTable>
</template>
